import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { clearClinicSession } from '../../Utils/clinicAuth';
import ConfirmDialog from './ConfirmDialog';

/*
 * Same color-mix() scoped-style approach used across the v2 auth/subscription pages —
 * Tailwind's opacity modifier doesn't work on our var(--cv2-*)-based custom colors. Embedded
 * here (rather than relying on a host page's own scoped <style>) so this component is fully
 * self-contained wherever it's dropped in.
 */
const SCOPED_STYLES = `
  .cv2-logout-btn {
    color: color-mix(in srgb, var(--cv2-foreground) 55%, transparent);
    border-color: color-mix(in srgb, var(--cv2-foreground) 15%, transparent);
    transition: color 0.15s ease, border-color 0.15s ease;
  }
  .cv2-logout-btn:hover {
    color: var(--cv2-foreground);
    border-color: color-mix(in srgb, var(--cv2-foreground) 30%, transparent);
  }
`;

const DARK_CLASSES = "cv2-logout-btn border rounded-full px-4 h-9 font-spaceMono text-[10px] tracking-[0.8px] uppercase";
const LIGHT_CLASSES = "text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 rounded-full px-4 h-9 text-sm transition-colors";

const LogoutButton = ({ variant = 'dark', className, confirmMessage, onLogout }) => {
    const navigate = useNavigate();
    const [showConfirm, setShowConfirm] = useState(false);

    /*
     * onLogout runs after the token is cleared, for callers already rendered on /login — the
     * navigate is a no-op there, so nothing unmounts and the caller has to reset its own state
     * to get back to a usable form.
     */
    const performLogout = () => {
        clearClinicSession();
        navigate('/login', { replace: true });
        if (onLogout) onLogout();
    };

    const handleClick = () => {
        if (confirmMessage) {
            setShowConfirm(true);
        } else {
            performLogout();
        }
    };

    return (
        <>
            {variant === 'dark' && <style>{SCOPED_STYLES}</style>}
            <button
                type="button"
                onClick={handleClick}
                className={className || `${variant === 'light' ? LIGHT_CLASSES : DARK_CLASSES} inline-flex items-center gap-2`}
            >
                <LogOut className="w-4 h-4" strokeWidth={1.75} />
                Log Out
            </button>

            {showConfirm && (
                <ConfirmDialog
                    variant={variant}
                    title="Log out?"
                    message={confirmMessage}
                    confirmLabel="Log Out"
                    cancelLabel="Cancel"
                    onConfirm={performLogout}
                    onCancel={() => setShowConfirm(false)}
                />
            )}
        </>
    );
};

export default LogoutButton;
